import {
  Autotractor,
  Implement,
  ImplementArchetype,
  ImplementHeightTurningRadiusLimit,
  ScvInput,
  ScvOutput,
  Tractor,
  TractorArchetype,
} from "protos/atk/autotractor";
import {
  ImplementEffectiveConfig,
  RigEffectiveConfig,
} from "protos/atk/effective_config";
import { TFunction } from "i18next";

export function fullModelName(
  t: TFunction,
  equipment: Tractor | Implement
): string {
  if (!equipment.archetype) {
    return t("utils.descriptor.none");
  }

  return t("views.autotractor.equipment.modelNameFormat", {
    make: equipment.archetype.make,
    model: equipment.archetype.model,
  });
}

export function friendlyScvOutputName(
  t: TFunction,
  scvOutput: ScvOutput
): string {
  if (scvOutput.description) {
    return t("views.autotractor.rigDetails.scvOutputNameWithDescription", {
      portNumber: scvOutput.port,
      description: scvOutput.description,
    });
  } else {
    return t("views.autotractor.rigDetails.scvOutputNameNoDescription", {
      portNumber: scvOutput.port,
    });
  }
}

export function implementNameForScvInputId(
  rig: Autotractor | RigEffectiveConfig,
  scvInputId: string
): string | undefined {
  if ("rigId" in rig) {
    // RigEffectiveConfig; note: can only identify inputs that are connected
    let implementId: string | undefined;

    for (const scv of rig.scvs) {
      if (scv.input?.id === scvInputId) {
        implementId = scv.implementId;
        break;
      }
    }

    for (const implement of rig.implements) {
      if (implement.implementId === implementId) {
        return implement.customerProvidedName;
      }
    }
  } else {
    // Autotractor itself
    for (const implement of rig.implements) {
      for (const archetypeScvInput of implement.archetype?.scvInputs?.inputs ??
        []) {
        if (archetypeScvInput.id === scvInputId) {
          return implement.name;
        }
      }

      for (const implementScvInput of implement.scvInputs?.inputs ?? []) {
        if (implementScvInput.id === scvInputId) {
          return implement.name;
        }
      }
    }
  }
}

export function functionNameForScvInputId(
  t: TFunction,
  rig: Autotractor | RigEffectiveConfig,
  scvInputId: string
): string | undefined {
  if ("rigId" in rig) {
    // RigEffectiveConfig; note: can only identify inputs that are connected
    for (const scv of rig.scvs) {
      if (scv.input?.id === scvInputId) {
        return scv.input.function;
      }
    }
  } else {
    // Autotractor itself
    for (const implement of rig.implements) {
      for (const archetypeScvInput of implement.archetype?.scvInputs?.inputs ??
        []) {
        if (archetypeScvInput.id === scvInputId) {
          return archetypeScvInput.function;
        }
      }

      for (const scvInput of implement.scvInputs?.inputs ?? []) {
        if (scvInput.id === scvInputId) {
          return scvInput.function;
        }
      }
    }
  }

  return t("utils.descriptors.unknown");
}

export function friendlyScvInputName(
  t: TFunction,
  implement: Implement | ImplementEffectiveConfig,
  scvInput: ScvInput
): string {
  if ("name" in implement) {
    // Implement itself
    return `${implement.name} / ${scvInput.function}`;
  } else {
    // ImplementEffectiveConfig
    return `${implement.customerProvidedName} / ${scvInput.function}`;
  }
}

export type TractorConfiguration = Omit<
  TractorArchetype,
  "id" | "make" | "model" | "series" | "updatedAt"
> &
  Omit<
    Tractor,
    | "archetype"
    | "updatedAt"
    | "customerId"
    | "customerUuid"
    | "captureCompleted"
  > & {
    raw: Tractor;
    fullModelName: string;
  };

export function resolveTractorConfiguration(
  t: TFunction,
  tractor: Tractor
): TractorConfiguration {
  const resolved: TractorConfiguration = {
    ...TractorArchetype.create(),
    name: "",
    raw: tractor,
    fullModelName: fullModelName(t, tractor),
  };

  if (tractor.archetype) {
    Object.assign(
      resolved,
      Object.fromEntries(
        Object.entries(tractor.archetype).filter(([_, v]) => v !== undefined)
      )
    );
  }

  Object.assign(
    resolved,
    Object.fromEntries(
      Object.entries(tractor).filter(([_, v]) => v !== undefined)
    )
  );

  return resolved;
}

export type ImplementConfiguration = Omit<
  ImplementArchetype,
  "id" | "make" | "model" | "series" | "updatedAt"
> &
  Omit<
    Implement,
    | "archetype"
    | "updatedAt"
    | "customerId"
    | "customerUuid"
    | "captureCompleted"
  > & {
    fullModelName: string;
    raw: Implement;
  };

export function resolveImplementConfiguration(
  t: TFunction,
  implement: Implement
): ImplementConfiguration {
  const resolved: ImplementConfiguration = {
    ...ImplementArchetype.create(),
    name: "",
    raw: implement,
    fullModelName: fullModelName(t, implement),
  };

  if (implement.archetype) {
    Object.assign(
      resolved,
      Object.fromEntries(
        Object.entries(implement.archetype).filter(([_, v]) => v !== undefined)
      )
    );
  }

  Object.assign(
    resolved,
    Object.fromEntries(
      Object.entries(implement).filter(([_, v]) => v !== undefined)
    )
  );

  return resolved;
}
export interface TurningRadiusLimitsViewModel {
  radius0to25M: number;
  radius25to50M: number;
  radius50to75M: number;
  radius75to100M: number;
}

export function turningRadiusLimitsToViewModel(
  limits: ImplementHeightTurningRadiusLimit[]
): TurningRadiusLimitsViewModel {
  // we intentionally squash the turning radius limit list into four values
  // in such a way that it never gets more optimistic than the stored object,
  // only less optimistic.  sort ascending by turning radius, write the first
  // radius limit into all four fields, then overwrite the turning radii with
  // higher values if the max height of another limit is anywhere in the
  // applicable range (e.g., if there's a limit of 5m at < 100%, and 8m at < 37%,
  // the 0-25% and 25-50% limits will be downgraded to 8m.)
  const limitsAscByRadius = limits.toSorted(
    (a, b) => a.minTurningRadiusM - b.minTurningRadiusM
  );

  const baseTurningRadiusM = limitsAscByRadius[0]?.minTurningRadiusM ?? 10;
  const turningRadiusViewModel: TurningRadiusLimitsViewModel = {
    radius0to25M: baseTurningRadiusM,
    radius25to50M: baseTurningRadiusM,
    radius50to75M: baseTurningRadiusM,
    radius75to100M: baseTurningRadiusM,
  };

  for (let index = 1; index < limitsAscByRadius.length; index++) {
    const thisLimit = limitsAscByRadius[index];

    // this should never happen but Typescript doesn't believe that
    if (!thisLimit) {
      continue;
    }

    turningRadiusViewModel.radius0to25M = Math.max(
      turningRadiusViewModel.radius0to25M,
      thisLimit.minTurningRadiusM
    );

    if (thisLimit.maxHeight > 25) {
      turningRadiusViewModel.radius25to50M = Math.max(
        turningRadiusViewModel.radius25to50M,
        thisLimit.minTurningRadiusM
      );
    }

    if (thisLimit.maxHeight > 50) {
      turningRadiusViewModel.radius50to75M = Math.max(
        turningRadiusViewModel.radius50to75M,
        thisLimit.minTurningRadiusM
      );
    }

    if (thisLimit.maxHeight > 75) {
      turningRadiusViewModel.radius75to100M = Math.max(
        turningRadiusViewModel.radius75to100M,
        thisLimit.minTurningRadiusM
      );
    }
  }

  return turningRadiusViewModel;
}
